/* eslint-disable @typescript-eslint/dot-notation */
import React from 'react'
import { Box, Button } from '@mui/material'
import { UserContactPreferenceRequestViewModel } from '@rsmus/ecp-userservice'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import FormPhoneTextField from 'src/rsmCoreComponents/components/DynamicForm/FormControls/FormPhoneTextField/FormPhoneTextField'
import { useFeatureFlag } from '../../../../utils/hooks/useFeatureFlag'
import DynamicForm from '../../../../rsmCoreComponents/components/DynamicForm/FormControls/DynamicForm'
import FormTextArea from '../../../../rsmCoreComponents/components/DynamicForm/FormControls/FormTextArea/FormTextArea'
import FormTextField from '../../../../rsmCoreComponents/components/DynamicForm/FormControls/FormTextField/FormTextField'
import {
  FormExclusions,
  FormValues,
} from '../../../../rsmCoreComponents/components/DynamicForm/models'
import { setContactPreferenceFormState } from '../../../../store/profile/profilePageSlice'
import { getSelectedContactMethod } from '../../../../store/profile/profileFormSlice'
import { getUserInfo } from '../../../../store/userInfo/userInfoSlice'
import {
  useSetUserContactPreferenceDetailsMutation,
  useGetUserContactPreferenceDetailsQuery,
} from '../../../../store/userService'
import ContactMethodRadioGroup from './ContactMethods/ContactMethodRadioGroup'
import NotificationFrequencySelect from './NotificationFrequencies/NotificationFrequencySelect'
import TimeZoneSelect from './TimeZones/TimeZoneSelect'

export interface DefaultFormValues {
  email: number
  timeZone: number
  mobileFrequency: number
  emailFrequency: number
}

const defaultFormValues: DefaultFormValues = {
  email: 1,
  timeZone: 3,
  mobileFrequency: 8,
  emailFrequency: 4,
}

export interface ContactPreferenceFormProps {
  excluded?: FormExclusions
  formRef?: any
  formType?: string
  useMobileSpacing?: boolean
}

const ContactPreferenceForm = ({
  excluded = {},
  formRef = undefined,
  formType = 'Edit',
  useMobileSpacing = undefined,
}: ContactPreferenceFormProps) => {
  const { t } = useTranslation()
  const userInfo = useSelector(getUserInfo)
  const { data } = useGetUserContactPreferenceDetailsQuery()
  const preferedContactMethodState: string = useSelector(
    getSelectedContactMethod,
  )
  const userProfileRedesignEnabled = useFeatureFlag('UserProfile_Redesign')
  const clientBusinessNameValue = userInfo.isEmployee ? 'RSMUS' : 'RSMUS Client'
  /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency */
  const preferredContactMethodState = useSelector(getSelectedContactMethod)
  const dispatch = useDispatch()
  const [setContactPreferenceDetails] =
    useSetUserContactPreferenceDetailsMutation()

  // User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency
  const hideNotificationFrequencies = true
  const formValues = {
    name: `${userInfo.firstName} ${userInfo.lastName}`,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    businessEmail: userInfo.emailAddress,
    businessPhone: data?.data.businessPhone,
    clientBusinessName: userProfileRedesignEnabled
      ? data?.data.clientBusinessName
      : clientBusinessNameValue,
    preferredContactMethod:
      data?.data.preferredContactMethod?.id ?? defaultFormValues.email,
    engagementActivityNotificationFrequency:
      data?.data.engagementActivityNotificationFrequency?.id ??
      defaultFormValues.emailFrequency,
    insightsActivityNotificationFrequency:
      data?.data.insightsActivityNotificationFrequency?.id ??
      defaultFormValues.emailFrequency,
    timeZone: data?.data.timeZone?.id ?? defaultFormValues.timeZone,
    visited: data?.data.visited,
  }
  const itemLayout = { item: true, lg: 6, md: 6, xs: 12 }
  const clientBusinessNameLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.ClientOrBusinessName')
    : t('UserProfile.ContactPreferenceForm.ClientOrBusinessNames')
  const businessPhoneLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.BusinessPhoneNumber')
    : t('UserProfile.ContactPreferenceForm.BusinessPhone')
  const businessEmailLabel = userProfileRedesignEnabled
    ? t('UserProfile.ContactPreferenceForm.Email')
    : t('UserProfile.ContactPreferenceForm.BusinessEmailAddress')

  const handleSubmitRequest = async (values: FormValues) => {
    /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency */
    let engagementFrequencyId =
      values['engagementActivityNotificationFrequency']
    let insightsFrequencyId = values['insightsActivityNotificationFrequency']
    // Apply defaults based on contact method if fields are hidden
    if (preferredContactMethodState === '2') {
      // Phone
      engagementFrequencyId = 5 // Weekly Text Alert
      insightsFrequencyId = 5 // Weekly Text Alert
    } else if (preferredContactMethodState === '1') {
      // Email
      engagementFrequencyId = 2 // Daily Email Digest
      insightsFrequencyId = 2 // Daily Email Digest
    }
    /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency */
    const request: UserContactPreferenceRequestViewModel = {
      clientBusinessName: values['clientBusinessName'],
      businessPhone: values['businessPhone'],
      engagementActivityNotificationFrequencyId:
        engagementFrequencyId /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency [previous value : values['engagementActivityNotificationFrequency']] */,
      insightsActivityNotificationFrequencyId:
        insightsFrequencyId /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency [previous value : values['insightsActivityNotificationFrequency']  */,
      preferredContactMethodId: values['preferredContactMethod'],
      timeZoneId: values['timeZone'],
      visited: true,
    }

    await setContactPreferenceDetails(request)
      .unwrap()
      .then(
        () => {
          dispatch(
            setContactPreferenceFormState({
              isViewOnlyMode: true,
              isSubmissionSuccessful: formType === 'Edit',
              isSubmissionFailed: undefined,
            }),
          )
        },
        () => {
          dispatch(
            setContactPreferenceFormState({
              isViewOnlyMode: true,
              isSubmissionSuccessful: undefined,
              isSubmissionFailed: true,
            }),
          )
        },
      )
  }

  const handleCancel = () => {
    dispatch(
      setContactPreferenceFormState({
        isViewOnlyMode: true,
        isSubmissionSuccessful: undefined,
        isSubmissionFailed: undefined,
      }),
    )
  }

  const footerComponent = () => (
    <Box>
      <Box component="span">
        <Button
          sx={{ my: 5, mx: 1 }}
          color="primary"
          variant="contained"
          type="submit"
          aria-label={t('UserProfile.ContactPreferenceForm.Submit')}
          data-testid="Btn_Profile_ContactPreferenceSubmit">
          {formType === 'Edit'
            ? t('UserProfile.ContactPreferenceForm.Save')
            : t('UserProfile.ContactPreferenceForm.Submit')}
        </Button>
      </Box>
      <Box component="span">
        <Button
          sx={{ my: 5, mx: 3 }}
          color="primary"
          variant="outlined"
          aria-label={t('UserProfile.ContactPreferenceForm.Cancel')}
          onClick={handleCancel}
          data-testid="Btn_Profile_ContactPreferenceCancel">
          {t('UserProfile.ContactPreferenceForm.Cancel')}
        </Button>
      </Box>
    </Box>
  )

  return (
    <DynamicForm
      formRef={formRef}
      containerLayout={
        userProfileRedesignEnabled
          ? { container: true }
          : {
              container: true,
              justifyContent: 'center',
              alignItems: 'center',
            }
      }
      overrideFormControlProps={
        userProfileRedesignEnabled
          ? {
              sx: {
                my: 0,
              },
            }
          : undefined
      }
      useMobileSpacing={useMobileSpacing}
      exclusions={excluded}
      definition={{
        ...(userProfileRedesignEnabled
          ? {
              name: {
                label: 'UserProfile.ContactPreferenceForm.Name',
                ariaLabel: 'UserProfile.ContactPreferenceForm.Name',
                readonly: true,
                component: FormTextField,
                itemLayout,
              },
            }
          : {
              firstName: {
                label: 'UserProfile.ContactPreferenceForm.FirstName',
                ariaLabel: 'UserProfile.ContactPreferenceForm.FirstName',
                readonly: true,
                component: FormTextField,
                itemLayout,
              },
              lastName: {
                label: 'UserProfile.ContactPreferenceForm.LastName',
                ariaLabel: 'UserProfile.ContactPreferenceForm.LastName',
                readonly: true,
                component: FormTextField,
                itemLayout,
              },
            }),
        ...((userProfileRedesignEnabled && !userInfo.isEmployee) ||
        !userProfileRedesignEnabled
          ? {
              clientBusinessName: {
                label: clientBusinessNameLabel,
                ariaLabel: clientBusinessNameLabel,
                readonly: !userProfileRedesignEnabled,
                maxLength: 60,
                component: userProfileRedesignEnabled
                  ? FormTextField
                  : FormTextArea,
                rows: 1,
                cols: 1,
                itemLayout,
              },
            }
          : {}),
        ...(userProfileRedesignEnabled
          ? {}
          : {
              timeZone: {
                label: 'UserProfile.ContactPreferenceForm.RegionOrTimeZone',
                ariaLabel: 'UserProfile.ContactPreferenceForm.RegionOrTimeZone',
                component: TimeZoneSelect,
                itemLayout,
              },
            }),
        businessPhone: {
          label: businessPhoneLabel,
          ariaLabel: businessPhoneLabel,
          required: preferedContactMethodState !== '1',
          maxLength: 20,
          nullable: preferedContactMethodState !== '1',
          validator: 'Phone',
          component: userProfileRedesignEnabled
            ? FormPhoneTextField
            : FormTextField,
          itemLayout,
        },
        businessEmail: {
          label: businessEmailLabel,
          ariaLabel: businessEmailLabel,
          readonly: true,
          component: FormTextField,
          itemLayout,
        },
        preferredContactMethod: {
          label: 'UserProfile.ContactPreferenceForm.PreferredContactMethod',
          ariaLabel: 'UserProfile.ContactPreferenceForm.PreferredContactMethod',
          component: ContactMethodRadioGroup,
          itemLayout: userProfileRedesignEnabled
            ? itemLayout
            : {
                item: true,
                lg: 12,
                md: 12,
                xs: 12,
              },
        },
        /* User story 522389 - Temporary change to hide Engagement and Insights activity notification frequency */
        ...(hideNotificationFrequencies
          ? {}
          : {
              engagementActivityNotificationFrequency: {
                label:
                  'UserProfile.ContactPreferenceForm.EngagementActivityNotificationFrequency',
                ariaLabel:
                  'UserProfile.ContactPreferenceForm.EngagementActivityNotificationFrequency',
                component: NotificationFrequencySelect,
                itemLayout,
              },
              insightsActivityNotificationFrequency: {
                label:
                  'UserProfile.ContactPreferenceForm.InsightsActivityNotificationFrequency',
                ariaLabel:
                  'UserProfile.ContactPreferenceForm.InsightsActivityNotificationFrequency',
                component: NotificationFrequencySelect,
                itemLayout,
              },
            }),
      }}
      initialValues={formValues}
      handleSubmit={(values: FormValues) => {
        handleSubmitRequest({ ...values })
      }}
      footerComponent={excluded?.footer ? null : footerComponent()}
      rootTestId="Sec_Profile_ContactForm"
    />
  )
}

export default ContactPreferenceForm
